// BaseRenderer.jsx
// This file contains the base renderer class for image viewing components in HSA-KIT.
// It is the parent class for all image rendering components, providing common functionality

// Copyright HS Analysis GmbH, 2025
// Author: Viktor Eberhardt

// Framework imports
import React from "react";
import PropTypes from "prop-types";

// external imports
import autoBind from "react-autobind";

// HSA imports
import { CommentType } from "./tools/CommentTool";
import { getBaseLog } from "../utils/CanvasUtil";
import Backend from "../../common/utils/Backend";

class BaseRenderer extends React.Component {
  constructor(props) {
    super(props);
    autoBind(this); // bind this to all class methods

    this.state = {
      t: 0,
      z: Math.floor(props.ome.sizeZ / 2),
      dragStart: null,
      ome: props.ome,
      initialized: false,
      initializedZ: false,
      initialScale: 1,
      minZ: 0,
      maxZ: props.ome.sizeZ - 1,
      fps: 0,
      // time playback
      sr: 60,
      // z playback
      zsr: 60,
      bufferSize: 100,
      playing: false,
      playingZ: false,
      playDirection: 1,
      playDirectionZ: 1,
      miniMapKey: 0,
      isBrightfield:
        props.ome &&
        props.ome.channels.length === 1 &&
        props.ome.channels[0].type === "brightfield",
      scaleBarData: null,
      miniMapReady: false,
      open: false,
      anchorEl: null,
      displayTimeBar: false,
      displayZStackBar: false,
    };
    this.heatMapTiles = [];
    this.trackedTiles = new Set();
    this.fileIdStateData = {};
    this.keepRendering = true;
    window.getMousePositionInImage = this.getMousePositionInImage;
    window.setZoomLevelForGridSize = this.setZoomLevelForGridSize;

    // frontend cached alpha channel images
    this.visibleImage = [];
    // frontend cached colored images
    this.coloredImages = [];
    this.imgRegistered = true;

    // fps history to smooth fps counter update
    this.lastFps = Array.from(Array(10), () => 60);

    this.createBackgroundPattern();
  }

  /**
   * Default implementation of the componentDidMount lifecycle method, needs to be called by the child class.
   * Sets a flag indicating that the component is mounted.
   */
  componentDidMountDefault() {
    this._isMounted = true;
    Backend.trackViewedFile(this.props.fileId);

    Backend.getViewedTiles(this.props.fileId).then((tiles) => {
      if (tiles) {
        // Add missing parent tiles for each tile
        const tileSet = new Map();
        tiles.forEach((tile) => {
          // Use a unique key for each tile
          const key = `${tile.level},${tile.x},${tile.y}`;
          tileSet.set(key, tile);
        });

        tiles.forEach((tile) => {
          for (let parentLevel = 0; parentLevel < tile.level; parentLevel++) {
            // Calculate parent tile coordinates
            const scale = Math.pow(2, tile.level - parentLevel);
            const parentX = Math.floor(tile.x / scale);
            const parentY = Math.floor(tile.y / scale);
            const parentKey = `${parentLevel},${parentX},${parentY}`;
            if (!tileSet.has(parentKey)) {
              tileSet.set(parentKey, {
                level: parentLevel,
                x: parentX,
                y: parentY,
              });
            }
          }
        });

        // Convert back to array and set the state
        this.heatMapTiles = Array.from(tileSet.values());
      } else {
        console.warn("No viewed tiles found for fileId:", this.props.fileId);
      }
    });
  }

  /**
   * Lifecycle method to handle cleanup when the component is about to unmount, needs to be called by the child class.
   * Sets the `_isMounted` flag to `false` to indicate the component is no longer mounted.
   */
  componentWillUnmountDefault() {
    this._isMounted = false;
  }

  /**
   * Tracks the currently viewed region in the image viewer by determining the center tile
   * at a computed pyramid level and recording it if not already tracked.
   *
   * - Computes the visible pyramid level based on the current scale.
   * - Determines the center of the visible area in image coordinates.
   * - Identifies the tile containing the center point at the computed level.
   * - Tracks the tile if it hasn't been tracked yet, updates the heat map, and redraws the minimap.
   */
  trackAllViewedRegions() {
    // Compute the current visible pyramid level based on scale
    let lv = Math.floor(
      getBaseLog(2, (this.getScale() / this.state.initialScale) * 2)
    );
    // Track one higher level
    lv = Math.min(lv + 1, this.props.ome.maxLevel + 4);
    if (lv < 0 || lv === Infinity) lv = 0;

    const cols = Math.pow(2, lv);

    // Calculate the visible rectangle in image coordinates
    const centerX =
      (this.getPosition().x + this.canvas.width / 2) / this.getScale();
    const centerY =
      (this.getPosition().y + this.canvas.height / 2) / this.getScale();
    const rectWidth = this.canvas.width / 2 / this.getScale();
    const rectHeight = this.canvas.height / 2 / this.getScale();
    const rectLeft = centerX - rectWidth / 2;
    const rectTop = centerY - rectHeight / 2;
    const rectRight = centerX + rectWidth / 2;
    const rectBottom = centerY + rectHeight / 2;

    // For all tiles at this level, check intersection with the rectangle
    for (let x = 0; x < cols; x++) {
      for (let y = 0; y < cols; y++) {
        // Tile bounds in image coordinates
        const tileLeft = (x / cols) * this.vw;
        const tileTop = (y / cols) * this.vh;
        const tileRight = ((x + 1) / cols) * this.vw;
        const tileBottom = ((y + 1) / cols) * this.vh;

        // Check intersection
        if (
          tileLeft < rectRight &&
          tileRight > rectLeft &&
          tileTop < rectBottom &&
          tileBottom > rectTop
        ) {
          const { x: rx, y: ry } = this.getRotatedTileCoords(x, y, cols);
          const tileKey = `${this.props.fileId}_${lv}_${rx}_${ry}`;
          if (!this.trackedTiles.has(tileKey)) {
            this.trackedTiles.add(tileKey);
            const params = {
              fileId: this.props.fileId,
              level: lv,
              x: rx,
              y: ry,
            };
            Backend.trackViewedTile(params);
            this.heatMapTiles.push(params);
            if (this.miniMapRef && this.miniMapRef.current) {
              this.miniMapRef.current.draw();
            }
          }
        }
      }
    }
  }

  /**
   * Calculates the page index for a given channel based on the current time (t),
   * depth (z), and the OME (Open Microscopy Environment) metadata.
   *
   * @param {number} c - The channel index for which the page is being calculated.
   * @returns {number} The calculated page index for the specified channel.
   */
  getPageForChannel(c) {
    const { t, z } = this.state;
    const { ome } = this.props;

    return (
      Math.round(t) * ome.channels.length * ome.sizeZ +
      Math.round(z) * ome.channels.length +
      c
    );
  }

  /**
   * Creates a background pattern using an HTML canvas element.
   * The pattern consists of diagonal lines drawn on a transparent background.
   * The canvas is configured with a width and height of 30 pixels.
   * The resulting pattern is stored in the `backgroundPattern` property of the class.
   */
  createBackgroundPattern() {
    this.backgroundPattern = document.createElement("canvas");
    const patternContext = this.backgroundPattern.getContext("2d");

    // Give the pattern a width and height of 50
    const w = 30;
    const h = 30;
    this.backgroundPattern.width = w;
    this.backgroundPattern.height = h;

    // Give the pattern a background color and draw an arc
    patternContext.fillStyle = "transparent";
    patternContext.strokeStyle = "#999";
    patternContext.fillRect(0, 0, w, h);
    patternContext.lineWidth = 1;
    patternContext.moveTo(-w / 2, h);
    patternContext.lineTo(w, -h / 2);
    patternContext.moveTo(0, h + h / 2);
    patternContext.lineTo(w + w / 2, 0);
    patternContext.stroke();
  }

  /**
   * Safely updates the component's state only if the component is currently mounted.
   *
   * @param {Object} stateObject - An object representing the new state to be merged with the current state.
   * @param {Function} [callback] - An optional callback function to be executed after the state is updated.
   */
  setMountedState(stateObject, callback) {
    if (this._isMounted) {
      this.setState(stateObject, callback);
    }
  }

  /**
   * Adjusts the zoom level of the canvas to a specified factor.
   *
   * @param {number} factor - The factor by which to scale the zoom level.
   *                           A value greater than 1 zooms in, while a value
   *                           less than 1 zooms out.
   */
  zoomToFactor(factor) {
    const wsiPixelSize = this.props.ome.physicalSizeX; // for example 4.0 microns and equal to scale 1.0
    const pixelSize1x = 4 / 1_000_000; // microns equal to 1x 1 beaing the factor

    let scaleTarget = (wsiPixelSize / pixelSize1x) * factor; // microns

    this.zoomTo = scaleTarget;
    this.zoomPoint = { x: this.canvas.width / 2, y: this.canvas.height / 2 };
    this.zoomDirection =
      this.getScale() < scaleTarget ? scaleTarget : -scaleTarget;
  }

  /**
   * Calculates a position in the image based on the specified corner or center position.
   *
   * @param {number} position - The position to calculate:
   *   - `0` for the top-left corner,
   *   - `1` for the bottom-right corner,
   *   - `2` for the center.
   * @returns {Object} An object representing the calculated position in image coordinates:
   *   - `x` {number}: The x-coordinate in the image.
   *   - `y` {number}: The y-coordinate in the image.
   */
  getPositionInImage(position) {
    // calculate corner points top left (position==0), bottom right (position==1), center (position==2)
    let X;
    let Y;
    if (position === 0) {
      // top left
      X = this.canvas.getBoundingClientRect().left;
      Y = this.canvas.getBoundingClientRect().top; //64 - this.canvas.offsetTop + 32; // 32 is half of blue header
    } else if (position === 1) {
      // bottom right
      X = this.canvas.getBoundingClientRect().right;
      Y = this.canvas.getBoundingClientRect().bottom;
      //- this.canvas.offsetTop + 32; // 32 is half of blue header
    } else {
      // center
      X =
        (this.canvas.getBoundingClientRect().left +
          this.canvas.getBoundingClientRect().right) /
        2;
      Y =
        (this.canvas.getBoundingClientRect().top +
          this.canvas.getBoundingClientRect().bottom) /
        2;
    }

    // calculate mouse position in image coordinates
    let p1 = this.getPointInCanvas({
      x: X - this.canvas.getBoundingClientRect().left,
      y: Y - this.canvas.getBoundingClientRect().top,
    });
    let p2 = this.getPointInCanvas({ x: 0, y: 0 });
    let p3 = this.getPosition();
    p1.x += p2.x - p3.x / this.getScale();
    p1.y += p2.y - p3.y / this.getScale();

    return p1;
  }

  /**
   * Converts the given coordinates (x, y) into canvas coordinates
   * based on the current transformation matrix of the canvas context.
   *
   * @param {number} x - The x-coordinate in the original coordinate system.
   * @param {number} y - The y-coordinate in the original coordinate system.
   * @returns {{x: number, y: number}} An object containing the transformed
   * x and y coordinates in the canvas coordinate system.
   */
  getPointInCanvasCoord(x, y) {
    let pt = {
      x: Math.round(x * this.ctx.getTransform().a + this.ctx.getTransform().e),
      y: Math.round(y * this.ctx.getTransform().d + this.ctx.getTransform().f),
    };
    return pt;
  }

  /**
   * Transforms a given point's coordinates to the canvas's coordinate system.
   *
   * @param {Object} p - The point to transform.
   * @param {number} p.x - The x-coordinate of the point.
   * @param {number} p.y - The y-coordinate of the point.
   * @returns {Object} An object containing the transformed coordinates.
   * @returns {number} return.x - The transformed x-coordinate, or -1 if an error occurs.
   * @returns {number} return.y - The transformed y-coordinate, or -1 if an error occurs.
   */
  getPointInCanvas(p) {
    try {
      let tp = this.ctx.transformedPoint(p.x, p.y);
      return {
        x: tp.x,
        y: tp.y,
      };
    } catch (ex) {
      // Skip this frame
      return {
        x: -1,
        y: -1,
      };
    }
  }

  /**
   * Determines if the mouse is currently busy by checking the cursor style of the canvas.
   *
   * @returns {boolean} - Returns `true` if the cursor style is not "default", indicating the mouse is busy; otherwise, `false`.
   */
  mouseBusy() {
    return !this.canvas.style.cursor !== "default";
  }

  /**
   * Calculates the middle position of the canvas in canvas coordinates
   * and updates the `mousePos` property with the center point.
   *
   * The method determines the top-left and bottom-right points of the canvas,
   * computes the center point, and assigns it to `this.mousePos`.
   */
  middleMousePos() {
    let p1 = this.getPointInCanvas({ x: 0, y: 0 });
    let p2 = this.getPointInCanvas({
      x: this.canvas.width,
      y: this.canvas.height,
    });
    let centerX = p1.x + (p2.x - p1.x) / 2;
    let centerY = p1.y + (p2.y - p1.y) / 2;
    this.mousePos = { x: centerX, y: centerY };
  }

  /**
   * Handles the zooming functionality based on mouse wheel events.
   * Adjusts the zoom level by calculating a new scale value and clamping it
   * within the allowed minimum and maximum scale range. Prevents default
   * scrolling behavior of the event.
   *
   * @param {WheelEvent} event - The mouse wheel event triggering the zoom action.
   */
  onScaleOnly(event) {
    let delta = 0;

    if (event.wheelDelta) {
      /* IE/Opera. */
      delta = -(event.wheelDelta / 120);
    } else if (event.detail) {
      /* Mozilla */
      delta = event.detail / 3;
    }

    if (delta) {
      // zoom in (1) or out (-1)
      this.zoomDirection = delta < 0 ? 1 : -1;

      // set destination scale value
      let newZoom = this.getScale() * Math.pow(2, this.zoomDirection);

      // Get min and max scale
      const { minScale, maxScale } = this.computeMinMaxScale();

      // Clamp the new zoom value
      newZoom = Math.max(minScale, Math.min(maxScale, newZoom));

      this.zoomTo = newZoom;
      this.zoomValueOut = newZoom;
      // redraw UI
      this.forceUpdate();
    }

    // prevent other scroll actions
    if (event.preventDefault) {
      event.preventDefault();
    }
    // event.returnValue = false;
  }

  /**
   * Retrieves the current scale factor of the canvas context.
   * The scale factor is determined by the horizontal scaling value (a)
   * from the context's transformation matrix.
   *
   * @returns {number} The scale factor. Returns 1 if the context (`ctx`) is not defined.
   */
  getScale() {
    return this.ctx ? this.ctx.getTransform().a : 1;
  }

  /**
   * Retrieves the current position of the canvas context by inverting the translation values
   * from the context's transformation matrix.
   *
   * @returns {Object} An object containing the x and y coordinates of the position.
   * @returns {number} return.x - The x-coordinate of the position.
   * @returns {number} return.y - The y-coordinate of the position.
   */
  getPosition() {
    return {
      x: -this.ctx.getTransform().e,
      y: -this.ctx.getTransform().f,
    };
  }

  /**
   * Adjusts the canvas view to zoom out and fit the entire image within the visible area.
   *
   * This method performs the following steps:
   * 1. Centers the image on the canvas.
   * 2. Calculates the transformation offset to align the image with the canvas center.
   * 3. Scales the image to fit entirely within the canvas dimensions.
   * 4. Updates the component's state with the initial scale factor and marks it as initialized.
   */
  defaultZoomOut() {
    // center image first
    let pt = this.getPointInCanvas({
      x: (this.canvas.width - this.vw) / 2,
      y: (this.canvas.height - this.vh) / 2,
    });
    this.ctx.translate(pt.x, pt.y);
    // calc our transformation offset
    pt = this.getPointInCanvas({
      x: this.canvas.width / 2,
      y: this.canvas.height / 2,
    });
    // move to origin
    this.ctx.translate(pt.x, pt.y);
    // calc scale factor so we can fit the whole image on our screen
    let factor = Math.min(
      this.canvas.height / this.vh,
      this.canvas.width / this.vw
    );
    // perform scaling
    this.ctx.scale(factor, factor);
    // restore our offset
    this.ctx.translate(-pt.x, -pt.y);
    // save initial scale into our state
    this.setMountedState({
      initialized: true,
      initialScale: factor,
    });
  }

  /**
   * Moves the canvas view to a specified point.
   *
   * This method calculates the current center position of the canvas in world coordinates,
   * determines the offset to the destination point, and translates the canvas context
   * accordingly. It then forces a re-render of the screen and triggers the zoom/move action.
   *
   * @param {Object} e - The destination point in world coordinates.
   * @param {number} e.x - The x-coordinate of the destination point.
   * @param {number} e.y - The y-coordinate of the destination point.
   */
  moveTo(e) {
    // get curent position (center of screen) and transform into world coordinates
    let pt = this.getPointInCanvas({
      x: this.canvas.width / 2,
      y: this.canvas.height / 2,
    });
    // translate by the offset to the destination point
    this.ctx.translate(-(e.x - pt.x), -(e.y - pt.y));
    // redraw screen
    this.forceUpdate();
    this.zoomMoveAction();
  }

  /**
   * Moves the canvas view to center on the specified world coordinates (x, y).
   *
   * This method calculates the current center of the screen in world coordinates,
   * determines the offset required to move to the target coordinates, and applies
   * the translation. It then triggers a redraw of the screen and executes any
   * additional zoom or move actions.
   *
   * @param {number} x - The x-coordinate in world space to center the view on.
   * @param {number} y - The y-coordinate in world space to center the view on.
   */
  chainMoveTo(x, y) {
    // get curent position (center of screen) and transform into world coordinates
    let pt = this.getPointInCanvas({
      x: this.canvas.width / 2,
      y: this.canvas.height / 2,
    });
    // translate by the offset to the destination point
    this.ctx.translate(-(x - pt.x), -(y - pt.y));
    // redraw screen
    this.forceUpdate();
    this.zoomMoveAction();
  }

  /**
   * Handles the chained mouse move event for synchronized rendering across multiple views.
   * Depending on the fullscreen state, it updates the position of the mouse in either
   * the splitscreen or chained views by transforming the coordinates and invoking the
   * corresponding `chainMoveTo` method on the appropriate renderers.
   *
   * @param {number} x - The x-coordinate of the mouse event.
   * @param {number} y - The y-coordinate of the mouse event.
   * @param {MouseEvent} e - The mouse event object.
   */
  chainMouseMove(x, y, e) {
    if (this.props.isChained) {
      if (this.props.showFullscreen) {
        for (const value of this.props.splitscreenFileIds) {
          if (value !== this.props.fileId) {
            let ctx =
              this.props.rendererDict["Full" + value].miniMapRef.getCtx();
            let pt = ctx.transformedPoint(x, y);
            this.props.rendererDict["Full" + value].chainMoveTo(pt.x, pt.y, e);
          }
        }
      } else {
        for (const value of this.props.chainListFileIds) {
          if (value !== this.props.fileId) {
            let ctx = this.props.rendererDict[value].miniMapRef.getCtx();
            let pt = ctx.transformedPoint(x, y);
            this.props.rendererDict[value].chainMoveTo(pt.x, pt.y, e);
          }
        }
      }
    }
  }

  /**
   * Preloads the next set of frames based on the current play direction.
   * If the play direction is positive, it preloads frames forward up to the buffer size
   * or until the maximum frame index is reached. If the play direction is negative,
   * it preloads frames backward up to the buffer size or until the minimum frame index is reached.
   *
   * @method preloadNextFrames
   */
  preloadNextFrames() {
    if (this.state.playDirection > 0) {
      for (
        let t = this.state.t;
        t <
        Math.min(
          this.props.ome.sizeT - 1,
          this.state.t + this.state.bufferSize
        );
        t++
      ) {
        this.preloadFrame(this.state.z, t);
      }
    } else {
      for (
        let t = this.state.t;
        t >= Math.max(0, this.state.t - this.state.bufferSize);
        t--
      ) {
        this.preloadFrame(this.state.z, t);
      }
    }
  }

  /**
   * Preloads the next set of Z frames based on the current play direction.
   * If the play direction in the Z axis is positive, it preloads frames from the current Z position
   * up to the minimum of the buffer size or the maximum Z size.
   * If the play direction in the Z axis is negative, it preloads frames from the current Z position
   * down to the maximum of zero or the buffer size.
   *
   * @method preloadNextZFrames
   */
  preloadNextZFrames() {
    if (this.state.playDirectionZ > 0) {
      for (
        let z = this.state.z;
        z <
        Math.min(
          this.props.ome.sizeZ - 1,
          this.state.z + this.state.bufferSize
        );
        z++
      ) {
        this.preloadFrame(z, this.state.t);
      }
    } else {
      for (
        let z = this.state.z;
        z >= Math.max(0, this.state.z - this.state.bufferSize);
        z--
      ) {
        this.preloadFrame(z, this.state.t);
      }
    }
  }

  /**
   * Adjusts the zoom level of the canvas based on the provided delta value.
   * This method is used by children to implement the default zoom behavior.
   *
   * - Zooming is centered around the middle of the canvas.
   * - The zoom direction is determined by the sign of the delta value:
   *   - Positive delta zooms out.
   *   - Negative delta zooms in.
   * - The zoom level is constrained within a minimum and maximum range.
   *
   * @param {number} delta - The zoom delta value. A negative value zooms in, and a positive value zooms out.
   */
  zoomDeltaDefault(delta) {
    // no zooming enabled

    // zoom middle
    this.zoomPoint = { x: this.canvas.width / 2, y: this.canvas.height / 2 };
    // zoom in (1) or out (-1)
    this.zoomDirection = delta < 0 ? 1 : -1;

    // set destination scale value
    let newZoom = this.getScale() * Math.pow(1.1, this.zoomDirection);

    // max zoom out value
    if (newZoom < (0.75 * this.canvas.height) / this.vh) {
      newZoom = (0.75 * this.canvas.height) / this.vh;
    }

    // max zoom in value
    if (newZoom > (2 * this.vh) / this.canvas.height) {
      newZoom = (2 * this.vh) / this.canvas.height;
    }

    this.zoomTo = newZoom;

    // redraw ui
    this.forceUpdate();
  }

  /**
   * Handles the seek event and updates the current time or position.
   *
   * @param {Object} e - The event object triggered by the seek action.
   * @param {number} v - The value representing the new position or time to seek to.
   */
  onSeek(e, v) {
    this.updateT(v);
  }

  /**
   * Handles the change event and updates the state or performs an action
   * based on the provided event object.
   *
   * @param {Object} e - The event object triggered by the change event.
   */
  onChangeT(e) {
    this.updateT(e);
  }

  /**
   * Handles changes to the Z value and updates related components and persistent storage.
   *
   * @param {number} z - The new Z value to set.
   * @param {number} [minZ=this.state.minZ] - The minimum Z value (defaults to the current state's minZ).
   * @param {number} [maxZ=this.state.maxZ] - The maximum Z value (defaults to the current state's maxZ).
   */
  onChangeZValue(z, minZ = this.state.minZ, maxZ = this.state.maxZ) {
    this.updateZ(z, minZ, maxZ);
    this.props.tiles.setZLevel(z);
    this.props.persistentStorage.save("z", z); //remember for use after project reload or reopen
    this.props.persistentStorage.save("minZ", minZ); //remember for use after project reload or reopen
    this.props.persistentStorage.save("maxZ", maxZ); //remember for use after project reload or reopen
  }

  /**
   * Handles changes to the Z value from a slider or similar input.
   *
   * @param {Object} e - The event object triggered by the input change.
   * @param {number[]} v - An array containing the slider values:
   *   - v[0]: The minimum Z value.
   *   - v[1]: The current Z value.
   *   - v[2]: The maximum Z value.
   */
  onChangeZ(e, v) {
    let minZ = v[0];
    let z = v[1];
    let maxZ = v[2];
    this.onChangeZValue(z, minZ, maxZ);
  }

  /**
   * Handles the change event for the "sr" state.
   * Updates the component's state with the provided value.
   *
   * @param {any} e - The new value to set for the "sr" state.
   */
  onChangeSr(e) {
    this.setMountedState({ sr: e });
  }

  /**
   * Handles the change event for the ZSR (Zoom Scale Ratio) value.
   * Updates the component's state with the new ZSR value.
   *
   * @param {any} e - The new value for the ZSR.
   */
  onChangeZSr(e) {
    this.setMountedState({ zsr: e });
  }

  /**
   * Handles stepping through frames or time points in the viewer.
   *
   * @param {number} e - The step increment or decrement value. Positive values move forward,
   *                     and negative values move backward.
   */
  onStep(e) {
    this.updateT(
      Math.min(this.props.ome.sizeT - 1, Math.max(0, this.state.t + e))
    );
  }

  /**
   * Handles the adjustment of the Z-level (depth) within the allowed range.
   * Ensures the new Z-level is clamped between 0 and the maximum Z-level.
   *
   * @param {number} e - The step value to adjust the Z-level by. Can be positive or negative.
   */
  onStepZ(e) {
    let zLevel = Math.min(
      this.props.ome.sizeZ - 1,
      Math.max(0, this.state.z + e)
    );
    this.onChangeZValue(zLevel);
  }

  /**
   * Handles the play/pause functionality and updates the component's state accordingly.
   *
   * @param {boolean} e - Indicates whether the play state is active (true) or paused (false).
   * @param {string} dir - The direction of playback (e.g., "forward" or "reverse").
   */
  onPlayPause(e, dir) {
    this.setMountedState({ playing: e, playingZ: false, playDirection: dir });
  }

  /**
   * Handles the play/pause functionality for the Z-axis.
   *
   * @param {boolean} e - Indicates whether the Z-axis is currently playing (true) or paused (false).
   * @param {number} dir - The direction of play for the Z-axis. Typically, 1 for forward and -1 for reverse.
   */
  onPlayPauseZ(e, dir) {
    this.setMountedState({
      playingZ: e,
      playing: false,
      playDirectionZ: dir,
    });
  }

  /**
   * Toggles the state of a given key and saves the updated state to persistent storage.
   *
   * @param {string} key - The key representing the state to toggle. If the key starts with "show",
   *                       it will not include the `fileId` as an extender.
   *
   * Behavior:
   * - If `showFullscreen` is true, the key is appended with "Full" and optionally the `fileId`.
   * - Otherwise, the key is appended only with the `fileId` (if applicable).
   *
   * The updated state is saved to `persistentStorage` and the component's state is updated.
   */
  onToggle(key) {
    let keyExtender = key.startsWith("show") ? "" : this.props.fileId;
    //e.g. "showMiniMap", true
    if (this.props.showFullscreen) {
      this.props.persistentStorage.save(
        key + "Full" + keyExtender,
        !this.state[key]
      );
    } else {
      this.props.persistentStorage.save(key + keyExtender, !this.state[key]);
    }
    this.setMountedState({ [key]: !this.state[key] });
  }

  /**
   * Retrieves the value of a specified layout element from the component's state.
   *
   * @param {string} key - The key corresponding to the layout element in the state.
   * @returns {*} The value of the specified layout element from the state e.g. "showMiniMap".
   */
  getLayoutElement(key) {
    return this.state[key];
  }

  /**
   * Calculates the page index based on the provided channel, z-slice, and timepoint.
   *
   * @param {number} c - The channel index.
   * @param {number} z - The z-slice index.
   * @param {number} t - The timepoint index.
   * @returns {number} The calculated page index.
   */
  getPage(c, z, t) {
    // calculate page index from coordinates
    return (
      Math.round(t) * this.props.ome.channels.length * this.props.ome.sizeZ +
      c * this.props.ome.sizeZ +
      Math.round(z)
    );
  }

  /**
   * Adjusts the zoom level of the canvas to the specified scale factor.
   * Centers the zoom operation on the middle of the canvas and determines
   * the zoom direction based on the current scale.
   *
   * @param {number} x - The target zoom scale factor.
   */
  zoomOneToX(x) {
    this.zoomTo = x;
    this.zoomPoint = { x: this.canvas.width / 2, y: this.canvas.height / 2 };
    this.zoomDirection = this.getScale() < x ? 1 : -1;

    setTimeout(() => this.forceUpdate(), 10);
  }

  /**
   * Splits the input text into an array of lines, preserving empty lines.
   * Each line corresponds to a paragraph separated by newline characters (`\n`).
   *
   * @param {string} text - The input text to be wrapped.
   * @returns {string[]} An array of strings, each representing a line or paragraph.
   */
  wrapText(text) {
    if (!text) return [""];

    const lines = [];
    const paragraphs = text.split("\n");

    for (const paragraph of paragraphs) {
      if (!paragraph) {
        lines.push("");
        continue;
      }
      lines.push(paragraph);
    }

    return lines;
  }

  /**
   * Draws comment annotations (ROIs) on the canvas.
   * Handles drawing shapes, text placement and formatting for different ROI types.
   *
   * @param {Object} commentLayer - The layer containing ROIs to draw
   * @param {Array} commentLayer.commentRois - Array of ROI objects to render
   */
  drawComments(commentLayer) {
    if (!commentLayer || !commentLayer.commentRois) return;

    this.ctx.globalAlpha = 1;
    this.ctx.fillStyle = "transparent";
    const lineWidth = 2 / this.ctx.getTransform().a;
    this.ctx.lineWidth = lineWidth;

    for (let roi of commentLayer.commentRois) {
      this.ctx.beginPath();
      this.ctx.strokeStyle = roi.type === "commentBox" ? "black" : roi.color;
      let points = roi.regions;
      if (points && points.length > 1) {
        for (let i = 0; i < points.length; i++) {
          this.ctx.lineTo(points[i][0], points[i][1]);
        }
      }
      if (
        roi.type === "rectangle" ||
        roi.type === "region" ||
        roi.type === "commentBox"
      ) {
        this.ctx.closePath();
      }
      this.ctx.stroke();

      this.ctx.globalAlpha = 0.85;
      this.ctx.fillStyle = roi.type === "commentBox" ? "white" : "transparent";
      this.ctx.fill();
      this.ctx.globalAlpha = 1;

      // Draw text labels
      if (roi.type === CommentType.COMMENT_BOX || roi.commentValue) {
        const text = roi.commentValue;

        this.ctx.save();

        const boundsWidth = roi.bounds.right - roi.bounds.left;
        const boundsHeight = roi.bounds.bottom - roi.bounds.top;
        let fontSize;

        // Calculate font size based on ROI type
        if (
          roi.type === CommentType.DISTANCE ||
          roi.type === CommentType.ARROW
        ) {
          const length = Math.sqrt(
            boundsWidth * boundsWidth + boundsHeight * boundsHeight
          );
          fontSize = Math.round(length * 0.04);
        } else if (roi.type === CommentType.COMMENT_BOX) {
          fontSize = Math.round(boundsHeight * 0.3); // Changed to only use height with smaller factor
        } else {
          fontSize = Math.round(Math.sqrt(boundsWidth * boundsHeight) * 0.06);
        }

        this.ctx.font = fontSize + "px Arial";

        // Handle text placement and drawing for different ROI types
        if (roi.type === CommentType.COMMENT_BOX) {
          const padding = fontSize * 0.5;
          const maxWidth = boundsWidth - padding * 2;

          // First calculate number of lines and line breaks
          const initialLines = this.wrapText(text, maxWidth);
          const numLines = initialLines.length;
          const numLineBreaks = (text.match(/\n/g) || []).length;

          // Double the initial font size
          fontSize = fontSize * 2;

          // Adjust font size based on number of lines
          if (numLines > 1 || numLineBreaks > 0) {
            // Scale down font size for multiple lines
            const totalLines = Math.max(numLines, numLineBreaks + 1);
            fontSize = Math.round(fontSize / totalLines);
          } else {
            // Single line text - make it moderately larger
            fontSize = Math.round(fontSize * 1.2); // Changed from fontSize * 2 to fontSize * 1.2
          }

          this.ctx.font = fontSize + "px Arial";
          const lineHeight = fontSize * 1.2;

          // Recalculate lines with new font size
          const lines = this.wrapText(text, maxWidth);

          this.ctx.textAlign = "left";
          this.ctx.textBaseline = "top";
          this.ctx.fillStyle = roi.color;

          lines.forEach((line, index) => {
            const x = roi.bounds.left + padding;
            const y = roi.bounds.top + padding + lineHeight * index;
            this.ctx.fillText(line, x, y);
          });
        } else if (
          roi.type === CommentType.ARROW ||
          roi.type === CommentType.DISTANCE
        ) {
          const p1 = { x: roi.regions[0][0], y: roi.regions[0][1] };
          const p2 = { x: roi.regions[1][0], y: roi.regions[1][1] };
          const centerX = (p1.x + p2.x) / 2;
          const centerY = (p1.y + p2.y) / 2;

          let angle = Math.atan2(p2.y - p1.y, p2.x - p1.x);
          if (angle > Math.PI / 2 || angle < -Math.PI / 2) {
            angle -= Math.PI;
          }

          this.ctx.translate(centerX, centerY);
          this.ctx.rotate(angle);
          this.ctx.textAlign = "center";
          this.ctx.textBaseline = "bottom";

          const paddingH = 4; // Padding based on font size
          const paddingV = 2;
          const metrics = this.ctx.measureText(text);
          const textHeight = fontSize;
          const verticalOffset = lineWidth * 4;

          // Draw white background
          this.ctx.fillStyle = "white";
          this.ctx.fillRect(
            -metrics.width / 2 - paddingH, // Left padding proportional to fontSize
            -textHeight - paddingV - verticalOffset, // Top padding
            metrics.width + paddingH * 2, // Total width includes fontSize-based padding
            textHeight + paddingV * 2 // Total height includes fontSize-based padding
          );

          // Draw text in ROI color
          this.ctx.fillStyle = roi.color;
          this.ctx.fillText(text, 0, -verticalOffset);
        } else {
          this.ctx.textAlign = "left";
          this.ctx.textBaseline = "top";
          const verticalOffset = lineWidth * 4;

          if (roi.type === CommentType.REGION) {
            let lowestY = roi.regions[0][1];
            let lowestX = roi.regions[0][0];
            roi.regions.forEach((point) => {
              if (point[1] > lowestY) {
                lowestY = point[1];
                lowestX = point[0];
              }
            });
            this.ctx.translate(lowestX, lowestY + verticalOffset);
          } else {
            this.ctx.translate(
              roi.bounds.left,
              roi.bounds.bottom + verticalOffset
            );
          }

          const paddingH = 4; // Padding based on font size
          const paddingV = 2;
          const metrics = this.ctx.measureText(text);
          const textHeight = fontSize;

          // Draw white background
          this.ctx.fillStyle = "white";
          this.ctx.fillRect(
            -paddingH,
            -paddingV,
            metrics.width + paddingH * 2,
            textHeight + paddingV * 2
          );

          // Draw text in ROI color
          this.ctx.fillStyle = roi.color;
          this.ctx.fillText(text, 0, 0);
        }

        this.ctx.restore();
      }
    }
  }

  /**
   * Renders a canvas element with specific styles and event handlers.
   *
   * @function renderCanvas
   * @returns {JSX.Element} The rendered canvas element.
   *
   * @property {Object} props - The component's props.
   * @property {Object} props.classes - The styles applied to the canvas.
   * @property {string} props.canvasId - The unique ID for the canvas element.
   * @property {boolean} props.showTimeBar - Determines if the time bar is displayed.
   * @property {number} props.timeLineHeight - The height of the time bar (in pixels).
   *
   * @event onContextMenu - Prevents the default context menu from appearing on right-click.
   * @event onMouseMove - Triggered when the mouse moves over the canvas.
   * @event onMouseLeave - Triggered when the mouse leaves the canvas area.
   */
  renderCanvas() {
    const { classes, showTimeBar } = this.props;
    return (
      <canvas
        id={this.props.canvasId}
        className={classes.canvas}
        style={{
          height: showTimeBar
            ? `calc(100% - ${this.props.timeLineHeight}px)`
            : "100%",
          userSelect: "none",
        }}
        onContextMenu={(event) => event.preventDefault()}
        onMouseDown={(e) => this.mousedown(e)}
        onMouseMove={() => {
          this.mouseOnCanvas = true;
        }}
        onMouseLeave={() => {
          this.mouseOnCanvas = false;
        }}
      />
    );
  }
}

BaseRenderer.propTypes = {
  FV: PropTypes.object,
  activeFileId: PropTypes.string,
  canvasId: PropTypes.string,
  chainListFileIds: PropTypes.array,
  classes: PropTypes.object,
  fileId: PropTypes.string,
  isActive: PropTypes.bool,
  isChained: PropTypes.bool,
  ome: PropTypes.object,
  persistentStorage: PropTypes.object,
  rendererDict: PropTypes.object,
  showFullscreen: PropTypes.bool,
  showTimeBar: PropTypes.bool,
  splitscreenFileIds: PropTypes.array,
  tiles: PropTypes.object, // from withTiles
  timeLineHeight: PropTypes.number,
};

export default BaseRenderer;
